import React, { useState, useEffect } from 'react'
import { History } from 'history'
import { useAccountChange } from 'reactUtils'
import { TableLoader } from 'uiComponents/loaders'
import { MessageKind } from 'uiComponents/messages'
import { InlineDataTable, HeaderRow, TableHeader, Cell, DataRow } from 'uiComponents/table'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { ActionButtonA } from 'uiComponents/buttons'
import { OpeningTimesServiceContext } from './context'
import { OpeningTimesListItem } from './openingTimesService'
import { convertTimeFormat } from 'utils/dates'
import { useTranslation } from 'translations'
import { actionColumnWidth } from 'uiComponents/table/actionColumnWidth'

interface LocationsListPageProps {
    history: History
    accountSlug: string
    setActiveHeader: (headerText: string) => void
    replaceTopMessages: (id: string, status: MessageKind, text: string) => void
    hasPermission: (permission: string, accountSlug: string) => boolean
    hasFeature: (feature: string, accountSlug: string) => boolean
}

export function OpeningTimesListPage(props: LocationsListPageProps) {
    const { t } = useTranslation()
    const openingTimesService = React.useContext(OpeningTimesServiceContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [locations, setLocations] = useState<OpeningTimesListItem[] | null>(null)

    async function getLocationsList() {
        setLoading(true)
        try {
            const data = await openingTimesService.listLocations(props.accountSlug)
            setLocations(data)
            setLoading(false)
        } catch {
            props.replaceTopMessages('unknown_error', 'error', t('venue.openingTimes.genericError'))
        }
    }

    useEffect(() => {
        getLocationsList()
        props.setActiveHeader(t('venue.openingTimes.list.title'))
    }, [])

    useAccountChange(props.accountSlug, () => getLocationsList())

    const columnWidths = [null, '15em', '15em', actionColumnWidth(t('common.edit'))]

    return (
        <InlineDataTable columnWidths={columnWidths} id="locations-list">
            <HeaderRow>
                <TableHeader>{t('venue.openingTimes.list.columns.location')}</TableHeader>
                <TableHeader>{t('venue.openingTimes.list.columns.openingHours')}</TableHeader>
                <TableHeader>{t('venue.openingTimes.list.columns.ticketsSoldUntil')}</TableHeader>
                <TableHeader nonInteractive />
            </HeaderRow>
            {loading && <TableLoader />}
            {!loading && !!locations && (
                <>
                    {locations.length === 0 ? (
                        <NoResultsRow text={t('venue.openingTimes.list.emptyState')} />
                    ) : (
                        <>
                            {locations.map((l) => (
                                <DataRow key={l.id} className="locationsRow">
                                    <Cell className="name" title={l.locationName}>
                                        {l.locationName}
                                    </Cell>
                                    <Cell>
                                        {l.openingTime && l.closingTime ? (
                                            <>
                                                {convertTimeFormat(l.openingTime)} - {convertTimeFormat(l.closingTime)}
                                            </>
                                        ) : (
                                            t('venue.openingTimes.list.notConfigured')
                                        )}
                                    </Cell>
                                    <Cell>{l.sellUntilTime ? convertTimeFormat(l.sellUntilTime) : t('venue.openingTimes.list.endOfDay')}</Cell>
                                    <Cell>
                                        <ActionButtonA
                                            className="edit-opening-times"
                                            kind="action"
                                            secondary
                                            href={`/account/${props.accountSlug}/venue/opening_times/default/${l.id}`}
                                        >
                                            {t('common.edit')}
                                        </ActionButtonA>
                                    </Cell>
                                </DataRow>
                            ))}
                        </>
                    )}
                </>
            )}
        </InlineDataTable>
    )
}
